import { useEffect, useState } from "react";
import { LogoutOutlined, MenuOutlined, UserOutlined } from "@ant-design/icons";
import { Dropdown } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { selectGoogleLogin, selectLogin } from "../../containers/Login/selector";
import { sessionCleared, userProfileUpdated } from "../../containers/Login/slice";
import {
  clearAuthSession,
  getAuthRole,
  getAuthUser,
  isAuthenticated,
} from "../../../utils/auth";
import { API_ENDPOINT } from "../../../utils/constants";
import request from "../../../utils/request";
import {
  HeaderBreadcrumb,
  HeaderLeft,
  HeaderLeftGroup,
  HeaderMenuButton,
  HeaderRight,
  HeaderTitle,
  ProfileMenu,
  ProfileMenuAvatar,
  ProfileMenuDivider,
  ProfileMenuEmail,
  ProfileMenuItem,
  ProfileMenuName,
  ProfileMenuTitle,
  ProfileMenuUser,
  ProfileMenuUserInfo,
  StyledHeader,
  UserAvatar,
  UserBlock,
  UserInfo,
  UserName,
  UserRole,
} from "./style";

interface PageHeaderProps {
  routeName: string;
  breadcrumbLabel?: string;
  userRole?: string;
  avatarUrl?: string;
  onMenuClick?: () => void;
}

function deriveBreadcrumb(pathname: string): string {
  const seg = pathname.split("/").filter(Boolean)[0];
  if (!seg) return "Home";
  return seg
    .split("-")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

export default function PageHeader({
  routeName,
  breadcrumbLabel,
  userRole,
  avatarUrl,
  onMenuClick,
}: PageHeaderProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: loginData } = useSelector(selectLogin);
  const { data: googleData } = useSelector(selectGoogleLogin);
  const [menuOpen, setMenuOpen] = useState(false);

  const authData = loginData ?? googleData;
  const user = authData ?? getAuthUser();
  const fullName = user?.full_name ?? "Guest";
  const email = user?.email ?? "";
  const role = authData?.role ?? getAuthRole();
  const roleLabel = userRole ?? (role ? role.toUpperCase() : "");
  const breadcrumb = breadcrumbLabel ?? deriveBreadcrumb(pathname);
  const resolvedAvatarUrl = avatarUrl ?? authData?.profile_pic_url ?? undefined;

  const storedPic = authData?.profile_pic_url;
  useEffect(() => {
    if (!isAuthenticated() || storedPic !== undefined) return;
    let active = true;
    (async () => {
      try {
        const res = await request<{ id: number; profile_image: string | null }>(
          `${API_ENDPOINT}profile/picture`,
        );
        if (active) {
          dispatch(
            userProfileUpdated({ profile_pic_url: res.profile_image ?? null }),
          );
        }
      } catch {
        // ignore — header falls back to the user's initial
      }
    })();
    return () => {
      active = false;
    };
  }, [storedPic, dispatch]);

  function handleUserDetails() {
    setMenuOpen(false);
    if (pathname !== "/user-detail") navigate("/user-detail");
  }

  function handleLogout() {
    setMenuOpen(false);
    clearAuthSession();
    dispatch(sessionCleared());
    navigate("/login");
  }

  const profileMenu = (
    <ProfileMenu>
      <ProfileMenuTitle>Profile Settings</ProfileMenuTitle>
      <ProfileMenuUser align="center" gap={10}>
        <ProfileMenuAvatar size={38} src={resolvedAvatarUrl}>
          {fullName.charAt(0)}
        </ProfileMenuAvatar>
        <ProfileMenuUserInfo vertical>
          <ProfileMenuName>{fullName}</ProfileMenuName>
          <ProfileMenuEmail>{email}</ProfileMenuEmail>
        </ProfileMenuUserInfo>
      </ProfileMenuUser>
      <ProfileMenuDivider />
      <ProfileMenuItem
        type="text"
        icon={<UserOutlined />}
        onClick={handleUserDetails}
      >
        User Details
      </ProfileMenuItem>
      <ProfileMenuItem
        type="text"
        icon={<LogoutOutlined />}
        onClick={handleLogout}
      >
        Logout
      </ProfileMenuItem>
    </ProfileMenu>
  );

  return (
    <StyledHeader>
      <HeaderLeftGroup align="center" gap={12}>
        {onMenuClick && (
          <HeaderMenuButton
            type="text"
            icon={<MenuOutlined />}
            onClick={onMenuClick}
            aria-label="Open menu"
          />
        )}
        <HeaderLeft vertical gap={2}>
          <HeaderTitle level={4}>{routeName}</HeaderTitle>
          <HeaderBreadcrumb>Home / {breadcrumb}</HeaderBreadcrumb>
        </HeaderLeft>
      </HeaderLeftGroup>
      <HeaderRight align="center" gap={18}>
        <Dropdown
          open={menuOpen}
          onOpenChange={setMenuOpen}
          trigger={["click"]}
          placement="bottomRight"
          popupRender={() => profileMenu}
        >
          <UserBlock align="center" gap={10}>
            <UserAvatar size={36} src={resolvedAvatarUrl}>
              {fullName.charAt(0)}
            </UserAvatar>
            <UserInfo vertical>
              <UserName>{fullName}</UserName>
              <UserRole>{roleLabel}</UserRole>
            </UserInfo>
          </UserBlock>
        </Dropdown>
      </HeaderRight>
    </StyledHeader>
  );
}
